// src/lib/audio/OpusStreamPlayer.ts

const NOOP = (_?: unknown) => { /* intentionally empty */ }

type Chunk = {
  channels: Float32Array[]
  offset: number
}

// Shared AudioContext for all PTT receive streams.
//
// Safari requires each AudioContext to be resumed from inside a user-gesture
// call stack. If we created a new context per stream (inside a WebSocket
// callback), Safari would keep it suspended forever and no PCM would play.
// So we hold one module-level context, created lazily, and unlocked whenever
// a user gesture is available.
//
// The sample rate hint matches Opus's native rate so the decoder output does
// not have to be resampled (and browsers can honor the hint to avoid a
// device-rate mismatch that would pitch-shift or degrade the audio).
const SHARED_SAMPLE_RATE = 48000
let sharedCtx: AudioContext | null = null

function createCtx(): AudioContext | null {
  const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext
  if (!Ctor) return null
  try {
    return new Ctor({ sampleRate: SHARED_SAMPLE_RATE }) as AudioContext
  } catch {
    try {
      return new Ctor() as AudioContext
    } catch {
      return null
    }
  }
}

export function getSharedAudioContext(): AudioContext | null {
  if (sharedCtx && sharedCtx.state !== 'closed') return sharedCtx
  sharedCtx = createCtx()
  return sharedCtx
}

/** Must be called from a user-gesture handler to unlock audio in Safari. */
export async function unlockPcmAudio(): Promise<void> {
  const ctx = getSharedAudioContext()
  if (!ctx) return
  if (ctx.state !== 'running') {
    try { await ctx.resume() } catch (err) { NOOP(err) }
  }
}

export class PcmStreamPlayer {
  private ctx: AudioContext | null = null
  private node: ScriptProcessorNode | null = null
  private gain: GainNode | null = null

  private queue: Chunk[] = []
  private requestedSampleRate: number
  private numChannels: number
  private bufferSize: number
  private volume: number
  private started = false

  constructor(opts: { sampleRate: number; numChannels: number; bufferSize?: number; initialVolume?: number }) {
    this.requestedSampleRate = opts.sampleRate
    this.numChannels = Math.max(1, opts.numChannels | 0)
    this.bufferSize = opts.bufferSize ?? 2048
    this.volume = opts.initialVolume ?? 1
  }

  get audioContext() {
    return this.ctx
  }

  get sampleRate() {
    return this.ctx?.sampleRate ?? this.requestedSampleRate
  }

  async start() {
    if (this.started) return
    this.started = true

    const ctx = getSharedAudioContext()
    if (!ctx) throw new Error('AudioContext not supported')
    this.ctx = ctx

    try { await ctx.resume() } catch (err) { NOOP(err) }

    const node = ctx.createScriptProcessor(this.bufferSize, 0, this.numChannels)
    this.node = node

    const gain = ctx.createGain()
    gain.gain.value = this.volume
    this.gain = gain

    node.onaudioprocess = (ev) => {
      try {
        for (let ch = 0; ch < this.numChannels; ch++) {
          ev.outputBuffer.getChannelData(ch).fill(0)
        }

        let writeIndex = 0
        let framesNeeded = ev.outputBuffer.length

        while (framesNeeded > 0 && this.queue.length) {
          const cur = this.queue[0]
          const curLen = cur.channels[0].length
          const available = curLen - cur.offset
          const take = Math.min(framesNeeded, available)

          for (let ch = 0; ch < this.numChannels; ch++) {
            const out = ev.outputBuffer.getChannelData(ch)
            const src = cur.channels[Math.min(ch, cur.channels.length - 1)]
            out.set(src.subarray(cur.offset, cur.offset + take), writeIndex)
          }

          cur.offset += take
          writeIndex += take
          framesNeeded -= take

          if (cur.offset >= curLen) this.queue.shift()
        }
      } catch (err) { NOOP(err) }
    }

    node.connect(gain)
    gain.connect(ctx.destination)
  }

  setVolume(v: number) {
    const next = Math.max(0, Math.min(1, v))
    this.volume = next
    if (this.gain) this.gain.gain.value = next
  }

  async resume() {
    const ctx = this.ctx
    if (!ctx) return
    if (ctx.state === 'suspended') {
      try { await ctx.resume() } catch (err) { NOOP(err) }
    }
  }

  enqueue(channelData: Float32Array[]) {
    if (!channelData?.length) return
    const len = channelData[0]?.length ?? 0
    if (!len) return

    const chans: Float32Array[] = []
    for (let i = 0; i < this.numChannels; i++) {
      chans.push(channelData[Math.min(i, channelData.length - 1)])
    }
    this.queue.push({ channels: chans, offset: 0 })
  }

  stop() {
    this.started = false
    this.queue = []

    try { this.node?.disconnect() } catch (err) { NOOP(err) }
    this.node = null

    try { this.gain?.disconnect() } catch (err) { NOOP(err) }
    this.gain = null

    // Do NOT close the shared context — other streams may be using it.
    this.ctx = null
  }
}
